/**
 * Project Form — Flow Studio adapter
 *
 * Thin host wrapper around the shared @bizfirst/project-management-react `ProjectForm`
 * (app-studio-project-app-unification, 2026-08-26). All field markup/validation display now
 * lives in the shared component; this file owns only what's genuinely Flow-Studio-specific:
 * the multi-step create orchestration (studioProjectApiClient.createWithStructure → Project +
 * App + Process + ProcessThread, then a follow-up update for fields createWithStructure doesn't
 * accept, then optionally auto-opening the designer on the first thread) and plain project
 * update. See this package's DevelopmentHistoryLog.md for why this orchestration stays here
 * rather than moving into the shared package (App Studio's own create flow is a different,
 * much simpler two-call composition — forcing one shared branch would either duplicate logic
 * back in or risk Flow Studio's working create path).
 */
import React from 'react';
import { Project, Process, ProcessThread, UpdateProjectRequest } from '@flow-studio/api';
import { projectApiClient, studioProjectApiClient, processApiClient, processThreadApiClient } from '@flow-studio/api';
import { useUIStore } from '@flow-studio/store';
import { ProjectForm as SharedProjectForm, type ProjectFormValues, type ProjectFormSubmitResult } from '@bizfirst/project-management-react';
import { userFacingProjectError } from './projectErrorMessage';

interface ProjectFormProps {
  project?: Project;
  onSuccess: (project: Project) => void;
  onCancel: () => void;
  autoOpenDesigner?: boolean;
  onOpenDesigner?: (project: Project, process: Process, processThread: ProcessThread) => void;
}

export const ProjectForm: React.FC<ProjectFormProps> = ({
  project,
  onSuccess,
  onCancel,
  autoOpenDesigner = false,
  onOpenDesigner,
}) => {
  const { showToast } = useUIStore();
  const isEdit = !!project;

  const handleUpdate = async (values: ProjectFormValues): Promise<ProjectFormSubmitResult> => {
    const updateRequest: UpdateProjectRequest = {
      ProjectID: project!.ProjectID,
      ...values,
      TenantID: project!.TenantID,
    };
    const response = await projectApiClient.update(updateRequest);

    if (response.success) {
      showToast('success', 'Project updated successfully!');
      onSuccess({ ...project!, ...values });
      return { success: true };
    }

    // The user sees only validation messages or a generic one. Raw API/SQL detail (which can name
    // database tables) is logged in development builds only.
    if (import.meta.env.DEV) console.error('[ProjectForm] update failed:', response.error);
    const message = userFacingProjectError(response.error);
    showToast('error', message);
    return { success: false, errorMessage: message };
  };

  const handleCreate = async (values: ProjectFormValues): Promise<ProjectFormSubmitResult> => {
    const createRequest = {
      projectName: values.Name,
      projectDescription: values.Description,
    };

    const structureResponse = await studioProjectApiClient.createWithStructure(createRequest);

    if (!structureResponse.success) {
      if (import.meta.env.DEV) console.error('[ProjectForm] createWithStructure failed:', structureResponse.error);
      const message = userFacingProjectError(structureResponse.error);
      showToast('error', message);
      return { success: false, errorMessage: message };
    }

    showToast('success', 'Project created successfully with App, Process, and Workflow!');
    const createdProjectId = structureResponse.data?.ProjectID;

    // Fetch the full project first — we need ALL its fields (including Name, TenantID, etc.)
    // before doing the follow-up update. The backend does a full entity replace on update, so
    // any field missing from the request body gets overwritten with its C# default (e.g. Name -> "").
    const projectResponse = createdProjectId
      ? await projectApiClient.getById({ id: createdProjectId })
      : { success: false, data: undefined };

    const hasExtraFields = values.Color || values.IconUrl || values.LongDescription || values.IsDefault || values.IsShared;

    if (createdProjectId && projectResponse.success && projectResponse.data && hasExtraFields) {
      const updateResponse = await projectApiClient.update({
        ...projectResponse.data,
        ProjectID: createdProjectId,
        TenantID: projectResponse.data.TenantID,
        Name: values.Name,
        Description: values.Description,
        Color: values.Color,
        IconUrl: values.IconUrl,
        LongDescription: values.LongDescription,
        IsDefault: values.IsDefault,
        IsShared: values.IsShared,
      });
      if (!updateResponse.success) {
        console.error('Failed to save extra project fields:', updateResponse.error);
        showToast('warning', 'Project created but some fields could not be saved.');
      }
    }

    if (!projectResponse.success || !projectResponse.data) {
      showToast('warning', 'Project created but could not fetch details. Please refresh.');
      return { success: true };
    }

    const createdProject: Project = {
      ...projectResponse.data,
      Name: values.Name,
      Description: values.Description,
      Color: values.Color || projectResponse.data.Color,
      IconUrl: values.IconUrl || projectResponse.data.IconUrl,
      LongDescription: values.LongDescription || projectResponse.data.LongDescription,
      IsDefault: values.IsDefault,
      IsShared: values.IsShared,
    };

    if (autoOpenDesigner && onOpenDesigner) {
      try {
        const processesResponse = await processApiClient.getByProject(createdProject.ProjectID);
        if (processesResponse.success && processesResponse.data && processesResponse.data.length > 0) {
          const firstProcess = processesResponse.data[0];
          const threadsResponse = await processThreadApiClient.getByProcess(firstProcess.ProcessID);
          if (threadsResponse.success && threadsResponse.data && threadsResponse.data.length > 0) {
            onOpenDesigner(createdProject, firstProcess, threadsResponse.data[0]);
            return { success: true };
          }
        }
      } catch (err) {
        console.error('Error loading designer:', err);
        showToast('warning', 'Project created but could not open designer automatically.');
      }
    }

    onSuccess(createdProject);
    return { success: true };
  };

  const handleSubmit = (values: ProjectFormValues): Promise<ProjectFormSubmitResult> => {
    // The native `required` check accepts whitespace-only input, so validate the trimmed name
    // here and never send a request for a blank name.
    const name = values.Name.trim();
    if (!name) {
      return Promise.resolve({ success: false, errorMessage: 'Project name is required.' });
    }
    const trimmed = { ...values, Name: name };
    return isEdit ? handleUpdate(trimmed) : handleCreate(trimmed);
  };

  return <SharedProjectForm project={project} onSubmit={handleSubmit} onCancel={onCancel} />;
};
